import { ColumnDef } from "@tanstack/react-table";
import { Link } from "@heroui/link";
import type { TFunction } from "i18next";
import { toWorkspacePath } from "@/features/Workspaces/workspaceRoutes";

import { formatNumber } from "@/lib/utils";

import { getAuditColumns } from "@/pages/a7/Audit/getAuditColumns";

export type A7FIAComposition = {
  id: number;
  date: Date;
  a7_fia_instrument_from_id: number;
  a7_fia_instrument_from_name: string;
  a7_fia_instrument_to_id: number;
  valorisation_part_du_sous_jacent: number;
  detention_du_fonds_dans_le_sous_jacent: number;
  vehicle?: string;
  a7_fia_time_serie_file_id?: number;
  a7_fia_issuer_id?: number;
  a7_fia_issuer_name?: string;
};

const tableName = "a7_fia_compositions";

export const getCompositionsColumns = (
  t: TFunction,
): ColumnDef<A7FIAComposition>[] => {
  const tTable = (key: string, defaultValue: string) =>
    t(`${tableName}.columns.${key}`, { ns: "tables", defaultValue });
  const tCommon = (key: string, defaultValue: string) =>
    t(`actions.${key}`, { ns: "common", defaultValue });

  return [
    {
      accessorKey: "id",
      header: tTable("id", "ID"),
    },
    {
      accessorKey: "date",
      header: tTable("date", "Date"),
    },
    {
      accessorKey: "a7_fia_instrument_from_name",
      header: tTable("a7_fia_instrument_from_name", "Fund"),
      cell: ({ row }) => {
        return (
          <Link
            href={toWorkspacePath(
              `/admin/a7-fia/a7_fia_instruments/${row.original.a7_fia_instrument_from_id}`,
            )}
            className="text-xs"
            underline="hover"
          >
            {row.getValue("a7_fia_instrument_from_name")}
          </Link>
        );
      },
    },
    {
      accessorKey: "a7_fia_instrument_to_id",
      header: tTable("a7_fia_instrument_to_id", "Financial instrument"),
      cell: ({ row }) => {
        return (
          <Link
            href={toWorkspacePath(
              `/admin/a7-fia/a7_fia_instruments/${row.original.a7_fia_instrument_to_id}`,
            )}
            className="text-xs"
            underline="hover"
          >
            {row.getValue("a7_fia_instrument_to_id")}
          </Link>
        );
      },
    },
    {
      accessorKey: "a7_fia_issuer_name",
      header: tTable("a7_fia_issuer_name", "Issuer"),
      cell: ({ row }) => {
        return (
          <Link
            href={toWorkspacePath(
              `/admin/a7-fia/a7_fia_issuers/${row.original.a7_fia_issuer_id}`,
            )}
            className="text-xs"
            underline="hover"
          >
            {row.getValue("a7_fia_issuer_name")}
          </Link>
        );
      },
    },
    {
      accessorKey: "a7_fia_time_serie_file_id",
      header: tTable("a7_fia_time_serie_file_id", "File"),
      cell: ({ row }) => {
        return (
          row.original.a7_fia_time_serie_file_id && (
            <Link
              href={toWorkspacePath(
                `/admin/a7-fia/a7_fia_time_serie_files/${row.original.a7_fia_time_serie_file_id}`,
              )}
              className="text-xs"
              underline="hover"
            >
              {row.getValue("a7_fia_time_serie_file_id")}
            </Link>
          )
        );
      },
    },
    {
      accessorKey: "valorisation_part_du_sous_jacent",
      header: () => (
        <div className="text-right">
          {tTable("valorisation_part_du_sous_jacent", "Valuation")}
        </div>
      ),
      cell: ({ getValue }) => {
        const value = getValue();
        return (
          value != null && (
            <div className="text-right">{formatNumber(value as number)}</div>
          )
        );
      },
    },
    {
      accessorKey: "detention_du_fonds_dans_le_sous_jacent",
      header: () => (
        <div className="text-right">
          {tTable("detention_du_fonds_dans_le_sous_jacent", "Holding")}
        </div>
      ),
      cell: ({ getValue }) => {
        const value = getValue();
        return (
          <div className="text-right">
            {typeof value === "number" ? (value * 100).toFixed(2) + "%" : ""}
          </div>
        );
      },
    },
    {
      accessorKey: "vehicle",
      header: () => (
        <div className="text-right">{tTable("vehicle", "Vehicle")}</div>
      ),
      cell: ({ getValue }) => (
        <div className="text-right">{getValue() as React.ReactNode}</div>
      ),
    },
    {
      id: "actions",
      cell: ({ row }) => {
        return (
          <div className="flex justify-end">
            <Link
              href={`${window.location.pathname}/${row.getValue("id")}`}
              className="text-xs"
              underline="hover"
            >
              {tCommon("edit", "Edit")}
            </Link>
          </div>
        );
      },
    },
  ];
};

export const getCompositionsAuditColumns = (t: TFunction) =>
  getAuditColumns<A7FIAComposition>(getCompositionsColumns(t)).filter(
    (column) => {
      const accessorKey =
        "accessorKey" in column
          ? String((column as { accessorKey?: unknown }).accessorKey ?? "")
          : "";
      return ![
        "a7_fia_issuer_name",
        "a7_fia_instrument_from_name",
        "id",
      ].includes(accessorKey);
    },
  );
